import { getSemaforoColor, getSemaforoBgColor } from "@/mocks/judicialRequirements";

interface ComplianceSemaphoreProps {
  semaforo: "verde" | "amarillo" | "rojo";
  motivo: string;
  fechaLimite: string;
  responsableName: string | null;
  insumosPendientes: number;
  insumosCompletos: number;
  insumosTotal: number;
}

export default function ComplianceSemaphore({
  semaforo,
  motivo,
  fechaLimite,
  responsableName,
  insumosPendientes,
  insumosCompletos,
  insumosTotal,
}: ComplianceSemaphoreProps) {
  const hoy = new Date("2026-06-10");
  const limite = new Date(fechaLimite);
  const diasRestantes = Math.ceil((limite.getTime() - hoy.getTime()) / (1000 * 60 * 60 * 24));

  const progressPct = insumosTotal > 0 ? Math.round((insumosCompletos / insumosTotal) * 100) : 0;

  return (
    <div className={`rounded-xl border p-4 ${getSemaforoBgColor(semaforo)}`}>
      {/* Header */}
      <div className="flex items-center gap-3 mb-3">
        <div className="flex items-center gap-2">
          <div className={`w-4 h-4 rounded-full ${getSemaforoColor(semaforo)} shadow-sm animate-pulse-subtle`} />
          <span className="text-sm font-bold capitalize">
            {semaforo === "verde" ? "Cumplimiento al día" : semaforo === "amarillo" ? "Requiere atención" : "Incumplimiento crítico"}
          </span>
        </div>
        <span className="ml-auto text-xs font-medium">
          {diasRestantes < 0
            ? `Vencido (${Math.abs(diasRestantes)} días)`
            : diasRestantes === 0
              ? "Vence HOY"
              : `${diasRestantes} días`}
        </span>
      </div>

      {/* Motivo */}
      <p className="text-xs mb-4 leading-relaxed">{motivo}</p>

      {/* Checklist */}
      <div className="space-y-2 mb-4">
        <CheckItem label="Responsable asignado" done={!!responsableName} />
        <CheckItem label="Fecha límite vigente" done={diasRestantes >= 0} />
        <CheckItem label="Insumos completos" done={insumosPendientes === 0} />
        <CheckItem label="Informe generado" done={semaforo === "verde"} />
        <CheckItem label="Sin acciones vencidas" done={semaforo !== "rojo"} />
      </div>

      {/* Progress bar de insumos */}
      {insumosTotal > 0 && (
        <div>
          <div className="flex items-center justify-between text-[11px] mb-1">
            <span className="text-foreground-600">Progreso de insumos</span>
            <span className="font-medium">{insumosCompletos}/{insumosTotal} completados</span>
          </div>
          <div className="w-full h-2 bg-white/60 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-700 ${
                progressPct === 100 ? "bg-emerald-500" : progressPct >= 50 ? "bg-amber-500" : "bg-red-500"
              }`}
              style={{ width: `${progressPct}%` }}
            />
          </div>
        </div>
      )}
    </div>
  );
}

function CheckItem({ label, done }: { label: string; done: boolean }) {
  return (
    <div className="flex items-center gap-2 text-xs">
      <span className={`w-4 h-4 flex items-center justify-center flex-shrink-0 ${done ? "text-emerald-500" : "text-red-400"}`}>
        <i className={`${done ? "ri-checkbox-circle-fill" : "ri-close-circle-fill"} text-sm`} />
      </span>
      <span className={done ? "text-foreground-700" : "text-foreground-500"}>{label}</span>
    </div>
  );
}